import type {
  StaffOperationsIntentPage,
  StaffOperationsIntentState,
  StaffOperationsSummary,
  StaffOperationsTaskId,
} from "@lafayette/schemas";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { useOperationsRuntime } from "./runtime";

// The reads of the Operations page. Nothing stays in the cache after the page lets go (`gcTime: 0`) and a failed read is not retried by
// the library: the page shows the error and offers a retry. Both reads run again when the page opens.

export const operationsKeys = {
  all: ["staff-operations"] as const,
  summary: ["staff-operations", "summary"] as const,
  jobs: (state: string | null, task: string | null) =>
    ["staff-operations", "jobs", state ?? "ALL", task ?? "ALL"] as const,
};

const READ_OPTIONS = {
  gcTime: 0,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnMount: "always",
} as const;

const JOBS_PAGE_SIZE = 25;

export function useOperationsSummary(enabled: boolean) {
  const { api } = useOperationsRuntime();
  return useQuery({
    queryKey: operationsKeys.summary,
    queryFn: ({ signal }): Promise<StaffOperationsSummary> => api.getSummary({ signal }),
    enabled,
    ...READ_OPTIONS,
  });
}

/** The job list, page by page: `fetchNextPage` sends the opaque `nextCursor` of the last page back as `cursor`. */
export function useOperationsJobs(
  filter: Readonly<{
    state: StaffOperationsIntentState | null;
    task: StaffOperationsTaskId | null;
  }>,
  enabled: boolean,
) {
  const { api } = useOperationsRuntime();
  return useInfiniteQuery({
    queryKey: operationsKeys.jobs(filter.state, filter.task),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }): Promise<StaffOperationsIntentPage> =>
      api.listJobs(
        {
          ...(filter.state === null ? {} : { state: [filter.state] }),
          ...(filter.task === null ? {} : { task: [filter.task] }),
          ...(pageParam === null ? {} : { cursor: pageParam }),
          limit: JOBS_PAGE_SIZE,
        },
        { signal },
      ),
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    ...READ_OPTIONS,
  });
}
